import { useEffect, useState } from "react";
import { Calendar, MapPin, Clock, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", guests: 1, notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).finally(() => setLoading(false));
  }, []);

  const submitRSVP = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/rsvp", { ...form, guests: Number(form.guests) || 1, event_id: selected.id });
      toast.success("RSVP confirmed! See you there.");
      setSelected(null);
      setForm({ name: "", email: "", phone: "", guests: 1, notes: "" });
    } catch (err) {
      toast.error("RSVP failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (d) => {
    try {
      return new Date(d).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    } catch { return d; }
  };

  return (
    <div className="container-wrap pt-16 md:pt-20 pb-24">
      <p className="overline mb-5"><span className="gold-divider align-middle mr-3" />Events</p>
      <h1 className="font-serif-display text-5xl md:text-6xl text-brand-dark max-w-3xl leading-[1.0]">
        Read-alouds, workshops, and block parties.
      </h1>
      <p className="mt-6 max-w-2xl text-brand-dark/70 text-lg leading-relaxed">
        Come hang with authors, take home free books, and discover the joy of reading in person. All events are free and open to the New Orleans community.
      </p>

      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="events-grid">
        {loading && <p className="text-brand-dark/60">Loading events…</p>}
        {!loading && events.length === 0 && <p className="text-brand-dark/60">No upcoming events. Check back soon!</p>}
        {events.map((ev) => (
          <div key={ev.id} data-testid={`event-card-${ev.id}`} className="bg-white rounded-3xl border border-brand-purple/15 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col">
            {ev.image_url && (
              <div className="h-52 overflow-hidden">
                <img src={ev.image_url} alt={ev.title} className="w-full h-full object-cover hover:scale-105 transition" />
              </div>
            )}
            <div className="p-6 flex-1 flex flex-col">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-brand-purple font-semibold mb-3">
                <Calendar className="w-4 h-4" /> {formatDate(ev.date)}
              </div>
              <h3 className="font-serif-display text-2xl text-brand-dark leading-tight mb-3">{ev.title}</h3>
              <p className="text-brand-dark/70 text-sm leading-relaxed mb-5 flex-1">{ev.description}</p>
              <div className="space-y-1.5 text-sm text-brand-dark/70 mb-5">
                <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-brand-gold" /> {ev.time}</p>
                <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-gold" /> {ev.location}</p>
                {ev.capacity && <p className="flex items-center gap-2"><Users className="w-4 h-4 text-brand-gold" /> {ev.capacity} spots</p>}
              </div>
              <button onClick={() => setSelected(ev)} data-testid={`rsvp-open-${ev.id}`} className="btn-primary self-start py-2.5 px-6 text-sm">
                RSVP
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* RSVP Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-dark/60 backdrop-blur p-4" onClick={() => setSelected(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitRSVP} className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl" data-testid="rsvp-modal">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-brand-purple font-bold">RSVP</p>
                <h3 className="font-serif-display text-2xl text-brand-dark mt-1">{selected.title}</h3>
              </div>
              <button type="button" onClick={() => setSelected(null)} className="text-brand-dark/60 hover:text-brand-dark" data-testid="rsvp-close-button"><X /></button>
            </div>
            <div className="space-y-3">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="rsvp-name-input" className="w-full border border-brand-purple/20 rounded-xl px-4 py-3 focus:outline-none focus:border-brand-purple text-sm" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="rsvp-email-input" className="w-full border border-brand-purple/20 rounded-xl px-4 py-3 focus:outline-none focus:border-brand-purple text-sm" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" data-testid="rsvp-phone-input" className="w-full border border-brand-purple/20 rounded-xl px-4 py-3 focus:outline-none focus:border-brand-purple text-sm" />
              <input type="number" min={1} max={10} value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} placeholder="Number of guests" data-testid="rsvp-guests-input" className="w-full border border-brand-purple/20 rounded-xl px-4 py-3 focus:outline-none focus:border-brand-purple text-sm" />
              <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes / questions (optional)" data-testid="rsvp-notes-input" className="w-full border border-brand-purple/20 rounded-xl px-4 py-3 focus:outline-none focus:border-brand-purple text-sm" />
            </div>
            <button type="submit" disabled={submitting} data-testid="rsvp-submit-button" className="btn-primary w-full mt-5 disabled:opacity-60">
              {submitting ? "Submitting…" : "Confirm RSVP"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
